import { Routes, Route, Navigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import ThemeInitializer from './components/ThemeInitializer'
import ProtectedRoute from './components/ProtectedRoute'
import RequirePermission from './components/RequirePermission'
import AppShell from './components/AppShell'
import DashboardPage from './pages/DashboardPage'
import MonthlyDashboardPage from './pages/MonthlyDashboardPage'
import CustomRangeDashboardPage from './pages/CustomRangeDashboardPage'
import InventoryDashboardPage from './pages/InventoryDashboardPage'
import LoginPage from './pages/LoginPage'
import SetupWizard from './pages/SetupWizard'
import AdminUsersPage from './pages/AdminUsersPage'
import AdminClassificationsPage from './pages/AdminClassificationsPage'
import { StoreAdminPage } from './pages/StoreAdminPage'
import MyStoresPage from './pages/MyStoresPage'
import AccessInboxPage from './pages/AccessInboxPage'
import MyProfilePage from './pages/MyProfilePage'
import AdminSettingsPage from './pages/AdminSettingsPage'
import AdminWidgetsPage from './pages/AdminWidgetsPage'
import DocsPage from './pages/DocsPage'
import ReportsPage from './pages/ReportsPage'
import AdventureBucksAuditPage from './pages/AdventureBucksAuditPage'
import BossReportsPage from './pages/BossReportsPage'
import AiQueryPage from './pages/AiQueryPage'
import AdminRoadmapPage from './pages/AdminRoadmapPage'
import RoleAdminPage from './pages/RoleAdminPage'
import PublicRoadmapPage from './pages/PublicRoadmapPage'
import ConfirmIdeaPage from './pages/ConfirmIdeaPage'
import UnsubscribePage from './pages/UnsubscribePage'
import NeverOutPage from './pages/NeverOutPage'
import NeverOutSilencePage from './pages/NeverOutSilencePage'
import PurchaseOrderPlanningPage from './pages/PurchaseOrderPlanningPage'
import TicketConversationPage from './pages/TicketConversationPage'
import InvitePage from './pages/InvitePage'
import { useAuthStore } from './stores/authStore'
import { API_BASE } from './lib/api'

const HOMEPAGE_ROUTES: Record<string, string> = {
    daily: '/dashboard',
    monthly: '/dashboard/monthly',
    custom: '/dashboard/custom',
}

function HomepageRedirect() {
    const token = useAuthStore(s => s.accessToken)
    const [target, setTarget] = useState<string | null>(null)

    useEffect(() => {
        if (!token) { setTarget('/dashboard'); return }
        fetch(API_BASE + '/api/me/preferences', {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                const pref = data?.homepageDashboard
                setTarget(HOMEPAGE_ROUTES[pref] ?? '/dashboard')
            })
            .catch(() => setTarget('/dashboard'))
    }, [token])

    if (!target) return null // Loading
    return <Navigate to={target} replace />
}

function App() {
    return (
        <>
            <ThemeInitializer />
            <Routes>
                {/* Public routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/setup" element={<SetupWizard />} />
                <Route path="/roadmap" element={<PublicRoadmapPage />} />
                <Route path="/roadmap/confirm/:token" element={<ConfirmIdeaPage />} />
                <Route path="/roadmap/unsubscribe/:token" element={<UnsubscribePage />} />
                <Route path="/never-out/silence/:token" element={<NeverOutSilencePage />} />
                <Route path="/roadmap/ticket/:token" element={<TicketConversationPage />} />
                <Route path="/invite/:token" element={<InvitePage />} />

                {/* Protected routes — nested under AppShell layout */}
                <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
                    <Route index element={<HomepageRedirect />} />
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/dashboard/monthly" element={<MonthlyDashboardPage />} />
                    <Route path="/dashboard/custom" element={<CustomRangeDashboardPage />} />
                    <Route path="/inventory" element={<RequirePermission permission="inventory.view"><InventoryDashboardPage /></RequirePermission>} />
                    <Route path="/never-out" element={<RequirePermission permission="neverout.view"><NeverOutPage /></RequirePermission>} />
                    <Route path="/purchase-orders/planning" element={<RequirePermission permission="purchaseorders.create"><PurchaseOrderPlanningPage /></RequirePermission>} />
                    <Route path="/reports" element={<RequirePermission permission="reports.view"><ReportsPage /></RequirePermission>} />
                    <Route path="/reports/adventure-bucks" element={<RequirePermission permission="loyalty.audit.view"><AdventureBucksAuditPage /></RequirePermission>} />
                    <Route path="/reports/boss" element={<RequirePermission permission="boss.reports.view"><BossReportsPage /></RequirePermission>} />
                    <Route path="/ai" element={<RequirePermission permission="ai.query"><AiQueryPage /></RequirePermission>} />
                    <Route path="/my-stores" element={<MyStoresPage />} />
                    <Route path="/inbox" element={<RequirePermission permission="access.approve"><AccessInboxPage /></RequirePermission>} />
                    <Route path="/profile" element={<MyProfilePage />} />
                    <Route path="/admin/users" element={<RequirePermission permission="users.admin"><AdminUsersPage /></RequirePermission>} />
                    <Route path="/admin/roles" element={<RequirePermission permission="roles.admin"><RoleAdminPage /></RequirePermission>} />
                    <Route path="/admin/classifications" element={<RequirePermission permission="roles.admin"><AdminClassificationsPage /></RequirePermission>} />
                    <Route path="/admin/stores" element={<RequirePermission permission="stores.admin"><StoreAdminPage /></RequirePermission>} />
                    <Route path="/admin/settings" element={<RequirePermission permission="system.settings"><AdminSettingsPage /></RequirePermission>} />
                    <Route path="/admin/widgets" element={<RequirePermission permission="dashboards.admin"><AdminWidgetsPage /></RequirePermission>} />
                    <Route path="/admin/roadmap" element={<RequirePermission permission="roadmap.admin"><AdminRoadmapPage /></RequirePermission>} />
                    <Route path="/docs/*" element={<DocsPage />} />
                    {/* Catch-all */}
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Route>
            </Routes>
        </>
    )
}

export default App

